/* eslint-disable complexity */
import React, { useMemo, Fragment, useEffect } from 'react';
import { Popover, Transition } from '@headlessui/react';
import { twMerge } from 'tailwind-merge';
import {
  Typography,
  Icon,
  SelectMenu,
  Button,
  Divider,
  TextField,
  DatePicker,
} from '@scale/scaleui';
import { IDate } from 'dashboard/hooks/useFilters';
import DateRangePicker from './DateRangePicker';
import moment from 'moment-timezone';
import { FilterObject, FieldMap } from '../utils/types';
import _ from 'lodash';
interface FilterComponentProps {
  filters: FilterObject;
  setFilters: (filters: FilterObject) => void;
  fieldMap: FieldMap;
  exactFieldOptionsMap: { [key: string]: string[] | { id: string; name: string }[] };
  fieldOptions: { id: string; name: string }[];
  setComponentOpen?: (open: boolean) => void;
  trackEvent?: () => void;
  quickFilters?: { name: string; filterState: FilterObject }[];
  andFilters?: boolean;
  inSidebar?: boolean;
  panelClassName?: string;
  hideButtonText?: boolean;
  maxOptionsHeight?: number;
}

export const DEFAULT_FILTER_OBJECT = { field: undefined, option: undefined, value: undefined };

const NUMBER_OPTIONS = [
  { id: 'gt', name: 'Greater than' },
  { id: 'lt', name: 'Less than' },
  { id: 'eq', name: 'Equal to' },
];

const DATE_OPTIONS = [
  { id: 'before', name: 'Before' },
  { id: 'between', name: 'Between' },
  { id: 'after', name: 'After' },
];

const FUZZY_SEARCH_OPTIONS = [
  { id: 'contains', name: 'Contains' },
  { id: 'doesNotContain', name: 'Does not contain' },
  { id: 'is', name: 'Is' },
  { id: 'isNot', name: 'Is not' },
];

const EXACT_SEARCH_OPTIONS = [
  { id: 'is', name: 'Is' },
  { id: 'isNot', name: 'Is not' },
];

const OPTION_TO_COPY: Record<string, string> = {
  gt: '>',
  lt: '<',
  eq: '=',
  contains: 'contains',
  doesNotContain: 'does not contain',
  is: 'is',
  isNot: 'is not',
  before: 'before',
  between: 'between',
  after: 'after',
  in: 'in',
};

const CONTAINS_ONLY = [{ id: 'contains', name: 'Contains' }];

const IN = [{ id: 'in', name: 'In' }];

const IN_ONLY = [{ id: 'is', name: 'In' }];

const IS_ONLY = [{ id: 'is', name: 'Is' }];

const OPTIONS_MAP = {
  number: NUMBER_OPTIONS,
  fuzzy: FUZZY_SEARCH_OPTIONS,
  exact: EXACT_SEARCH_OPTIONS,
  contains_only: CONTAINS_ONLY,
  in_only: IN_ONLY,
  is_only: IS_ONLY,
  date: DATE_OPTIONS,
  in: IN,
};

const FilterComponent: React.FC<FilterComponentProps> = ({
  filters,
  setFilters,
  fieldMap,
  exactFieldOptionsMap,
  fieldOptions,
  setComponentOpen,
  trackEvent = () => {},
  quickFilters,
  andFilters,
  inSidebar,
  panelClassName = '',
  hideButtonText,
  maxOptionsHeight = 32,
}) => {
  const createButtonMessage = useMemo(() => {
    const fullFilters = filters.filters.filter(
      filter => filter.field && filter.option && filter.value,
    );
    if (fullFilters.length === 0) {
      return 'Filter';
    }
    if (fullFilters.length === 1) {
      let value = fullFilters[0].displayValue ?? fullFilters[0].value;
      if (fieldMap[fullFilters[0].field!] === 'date') {
        // TODO: fix typing for dates
        const dateValue = value as unknown as moment.Moment | [moment.Moment, moment.Moment];
        const DATE_FORMAT = 'MM/DD/YYYY';
        if (fullFilters[0].option === 'between' && Array.isArray(dateValue)) {
          value = `${dateValue[0].format(DATE_FORMAT)} and ${dateValue[1].format(DATE_FORMAT)}`;
        } else if (!Array.isArray(dateValue)) {
          value = dateValue.format(DATE_FORMAT);
        } else {
          value = '';
        }
      }
      return `${_.capitalize(
        fieldOptions.find(option => fullFilters[0].field === option.id)?.name,
      )} ${OPTION_TO_COPY[fullFilters[0]!.option ?? '']} ${
        (value ?? '').length > 20 ? (value ?? '').slice(0, 20) + '...' : value
      }`;
    }
    if (fullFilters.length === 2) {
      return `${_.capitalize(
        fieldOptions.find(option => fullFilters[0].field === option.id)?.name,
      )}, ${_.capitalize(fieldOptions.find(option => fullFilters[1].field === option.id)?.name)}`;
    }
    if (fullFilters.length === 3) {
      return `${_.capitalize(
        fieldOptions.find(option => fullFilters[0].field === option.id)?.name,
      )}, ${_.capitalize(
        fieldOptions.find(option => fullFilters[1].field === option.id)?.name,
      )}, ${_.capitalize(fieldOptions.find(option => fullFilters[2].field === option.id)?.name)}`;
    }
    return `${_.capitalize(
      fieldOptions.find(option => fullFilters[0].field === option.id)?.name,
    )}, ${_.capitalize(
      fieldOptions.find(option => fullFilters[1].field === option.id)?.name,
    )}, and ${fullFilters.length - 2} more`;
  }, [filters, fieldOptions]);

  const currentFilters = new Set(filters.filters.map(f => f.field));
  const availableFields = fieldOptions.filter(f => !andFilters || !currentFilters.has(f.id));

  return (
    <div>
      <Popover className="relative">
        {({ open }) => {
          /* eslint-disable react-hooks/rules-of-hooks */
          useEffect(() => {
            if (setComponentOpen) {
              setComponentOpen(open);
            }
            if (open) {
              trackEvent();
            }
          }, [open]);
          return (
            <>
              <Popover.Button
                className={twMerge(
                  'scaleui inline-flex items-center rounded border-0 font-medium focus:outline-none transition-all h-fit pr-0.5 pl-0.5 py-0.5 text-sm hover:bg-neutral-300 bg-transparent text-neutral-600',
                  filters.filters.find(filter => filter.field && filter.option && filter.value) &&
                    'bg-primary-100 ',
                )}
              >
                <Typography.Paragraph2 className="flex items-center justify-evenly">
                  <Icon
                    icon={['far', 'filter']}
                    variant={
                      filters.filters.find(filter => filter.field && filter.option && filter.value)
                        ? 'primary'
                        : 'neutral'
                    }
                  />
                  {!inSidebar && (
                    <span
                      className={twMerge(
                        'block truncate pr-2',
                        filters.filters.find(
                          filter => filter.field && filter.option && filter.value,
                        )
                          ? 'text-primary-600'
                          : 'text-neutral-500',
                      )}
                    >
                      {!hideButtonText && createButtonMessage}
                    </span>
                  )}
                </Typography.Paragraph2>
              </Popover.Button>
              <Transition
                show={open}
                as={Fragment}
                leave="transition ease-in duration-100"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <Popover.Panel
                  className={twMerge(
                    'absolute z-20 w-[700px] py-1 mt-1 text-base rounded-lg shadow-lg bg-neutral-0 h-fit  ring-1 ring-neutral-900 ring-opacity-5 focus:outline-none sm:text-sm',
                    panelClassName,
                  )}
                  style={{ position: 'absolute' }}
                >
                  {filters.filters.length === 0 && quickFilters && quickFilters.length > 0 && (
                    <div className="flex flex-row gap-3 items-start pt-3 px-4">
                      <span className="pt-3">Quick Filters </span>
                      <div>
                        {quickFilters.map((quickFilter, i) => (
                          <div className="px-1 py-1" key={i}>
                            <Button
                              onClick={() => {
                                const filters = quickFilter.filterState;
                                setFilters({
                                  operator: filters.operator,
                                  filters: [...filters.filters],
                                });
                              }}
                              size="lg"
                              variant="neutral"
                              key={i}
                              className="px-2 py-1 text-neutral-500"
                            >
                              {quickFilter.name}
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="flex flex-col gap-3 py-3 px-4">
                    {filters.filters.map((filter, i) => {
                      const currentField = filter.field;
                      let currentOption = filter.option;
                      if (currentField !== undefined && !filter.option) {
                        const defaultOptionId = OPTIONS_MAP[fieldMap[currentField]][0]!.id;
                        setFilters({
                          ...filters,
                          filters: [
                            ...filters.filters.slice(0, i),
                            { ...filters.filters[i], option: defaultOptionId },
                            ...filters.filters.slice(i + 1),
                          ],
                        });
                        currentOption = defaultOptionId;
                      }

                      const currentValue = filter.value;
                      return (
                        <div className="flex flex-row gap-3 items-center" key={i}>
                          {i === 0 && <span className="pl-8">Where</span>}
                          {i === 1 && !andFilters && (
                            <SelectMenu<'and' | 'or'>
                              options={[
                                { id: 'and', name: 'and' },
                                { id: 'or', name: 'or' },
                              ]}
                              selectedOptionId={filters.operator}
                              optionsClassName="max-h-32"
                              className="w-[75px]"
                              setSelected={selectedId => {
                                setFilters({ ...filters, operator: selectedId });
                              }}
                            />
                          )}
                          {((i === 1 && andFilters) || i > 1) && (
                            <span
                              className={twMerge(
                                filters.operator === 'and' && 'pl-[50px]',
                                filters.operator === 'or' && 'pl-[60px]',
                              )}
                            >
                              {filters.operator}
                            </span>
                          )}

                          <SelectMenu<string>
                            options={
                              andFilters
                                ? !currentField
                                  ? (fieldOptions ?? []).filter(f => !currentFilters.has(f.id))
                                  : [
                                      fieldOptions!.find(f => f.id === currentField)!,
                                      ...(fieldOptions ?? []).filter(
                                        f => !currentFilters.has(f.id),
                                      ),
                                    ]
                                : fieldOptions ?? []
                            }
                            selectedOptionId={currentField ?? ''}
                            setSelected={selectedId => {
                              setFilters({
                                ...filters,
                                filters: [
                                  ...filters.filters.slice(0, i),
                                  { field: selectedId, option: undefined, value: undefined },
                                  ...filters.filters.slice(i + 1),
                                ],
                              });
                            }}
                            optionsClassName="max-h-32"
                            className="w-[150px]"
                          />

                          {currentField !== undefined && (
                            <>
                              <SelectMenu<string>
                                // @ts-ignore
                                options={OPTIONS_MAP[fieldMap[currentField]] ?? []}
                                selectedOptionId={currentOption ?? ''}
                                setSelected={selectedId => {
                                  setFilters({
                                    ...filters,
                                    filters: [
                                      ...filters.filters.slice(0, i),
                                      {
                                        ...filters.filters[i],
                                        option: selectedId,
                                        ...(fieldMap[currentField] === 'date' && {
                                          value: undefined,
                                        }),
                                      },
                                      ...filters.filters.slice(i + 1),
                                    ],
                                  });
                                }}
                                disabled={!currentField}
                                optionsClassName={`max-h-${maxOptionsHeight}`}
                                className="w-[150px]"
                              />
                              {fieldMap[currentField] === 'exact' ||
                              fieldMap[currentField] === 'in_only' ||
                              fieldMap[currentField] === 'in' ||
                              (fieldMap[currentField] === 'is_only' &&
                                exactFieldOptionsMap[currentField]) ||
                              (fieldMap[currentField] === 'fuzzy' && // sometimes we want fuzzy search enabled with exact options
                                currentOption === 'is' &&
                                exactFieldOptionsMap[currentField]) ? (
                                <SelectMenu<string>
                                  options={exactFieldOptionsMap[currentField]!.map(option =>
                                    typeof option === 'string'
                                      ? {
                                          id: option,
                                          name: option,
                                        }
                                      : option,
                                  )}
                                  selectedOptionId={(currentValue ?? '') as unknown as any}
                                  setSelected={
                                    ((selectedId: string) => {
                                      setFilters({
                                        ...filters,
                                        filters: [
                                          ...filters.filters.slice(0, i),
                                          {
                                            ...filters.filters[i],
                                            value: selectedId,
                                            displayValue: (() => {
                                              const option = exactFieldOptionsMap[
                                                currentField
                                              ]!.find(option =>
                                                typeof option === 'string'
                                                  ? option === selectedId
                                                  : option.id === selectedId,
                                              );
                                              if (typeof option === 'string') {
                                                return option;
                                              }
                                              return option?.name ?? selectedId;
                                            })(),
                                          },
                                          ...filters.filters.slice(i + 1),
                                        ],
                                      });
                                    }) as unknown as any
                                  }
                                  disabled={!currentField}
                                  optionsClassName={`max-h-${maxOptionsHeight}`}
                                  className="w-[150px]"
                                  multiple={fieldMap[currentField] === 'in'}
                                />
                              ) : fieldMap[currentField] === 'date' ? (
                                currentOption === 'between' ? (
                                  <DateRangePicker
                                    selectedDateFrom={currentValue?.[0] as unknown as IDate}
                                    selectedDateTo={currentValue?.[1] as unknown as IDate}
                                    handleChange={value => {
                                      setFilters({
                                        ...filters,
                                        filters: [
                                          ...filters.filters.slice(0, i),
                                          {
                                            ...filters.filters[i],
                                            value: value as unknown as string,
                                          },
                                          ...filters.filters.slice(i + 1),
                                        ],
                                      });
                                    }}
                                  />
                                ) : (
                                  <DatePicker
                                    value={(currentValue as unknown as IDate)?.toDate()}
                                    onChange={value => {
                                      setFilters({
                                        ...filters,
                                        filters: [
                                          ...filters.filters.slice(0, i),
                                          {
                                            ...filters.filters[i],
                                            value: moment(value) as unknown as string,
                                          },
                                          ...filters.filters.slice(i + 1),
                                        ],
                                      });
                                    }}
                                    placeholder="Select a date"
                                  />
                                )
                              ) : (
                                <TextField
                                  id="filter-value"
                                  className="w-[150px]"
                                  value={currentValue}
                                  onChange={e => {
                                    setFilters({
                                      ...filters,
                                      filters: [
                                        ...filters.filters.slice(0, i),
                                        { ...filters.filters[i], value: e.target.value },
                                        ...filters.filters.slice(i + 1),
                                      ],
                                    });
                                  }}
                                  type={fieldMap[currentField] === 'number' ? 'number' : 'text'}
                                />
                              )}
                            </>
                          )}

                          <Icon
                            icon={['fas', 'minus']}
                            size="sm"
                            onClick={() => {
                              setFilters({
                                ...filters,
                                filters: filters.filters.filter((_, j) => j !== i),
                              });
                            }}
                          />
                        </div>
                      );
                    })}
                    {filters.filters.length === 0 && <Divider />}
                    <Button
                      size="lg"
                      variant="transparent"
                      className="w-fit text-primary-600 shadow-none focus:outline-none focus:ring-0 focus:ring-offset-0 mt-8"
                      onClick={() => {
                        setFilters({
                          ...filters,
                          filters: [
                            ...filters.filters,
                            { field: availableFields?.[0].id, option: undefined, value: undefined },
                          ],
                        });
                      }}
                      disabled={availableFields.length === 0}
                    >
                      + Add filter condition
                    </Button>

                    {filters.filters.length > 0 && (
                      <>
                        <Divider />

                        <Button
                          size="lg"
                          variant="transparent"
                          className="w-fit text-neutral-500 shadow-none focus:outline-none focus:ring-0 focus:ring-offset-0"
                          onClick={() => {
                            setFilters({
                              ...filters,
                              filters: [],
                            });
                          }}
                        >
                          Remove all filters
                        </Button>
                      </>
                    )}
                  </div>
                </Popover.Panel>
              </Transition>
            </>
          );
        }}
      </Popover>
    </div>
  );
};

export default FilterComponent;
